import React, { useEffect, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import { useBooking } from '../../store/bookingStore';
import classes from './CreativeModal.module.css';
import ServiceCard from './ServiceCard';

const CreativeModal = ({
  open,
  onClose,
  studioCreativesData,
  initialPackage = null,
  openInDetailView = false,
  /** Index into studioCreativesData for which creative's packages to show (booking carousel). */
  activeCreativeIndex = 0,
}) => {
  const { bookingData, updateBookingData } = useBooking();
  const scrollContainerRef = useRef(null);
  const [canScrollLeft, setCanScrollLeft] = useState(false);
  const [canScrollRight, setCanScrollRight] = useState(false);
  const [selectedPackageDetail, setSelectedPackageDetail] = useState(null);

  useEffect(() => {
    if (open && initialPackage && openInDetailView) {
      setSelectedPackageDetail(initialPackage);
    } else if (open && !initialPackage) {
      setSelectedPackageDetail(null);
    }
  }, [open, initialPackage, openInDetailView]);

  const listLength = studioCreativesData.length;
  const resolvedIndex =
    listLength === 0
      ? 0
      : Math.min(Math.max(activeCreativeIndex, 0), listLength - 1);
  const primaryStudioCreative = studioCreativesData[resolvedIndex];
  const availablePackages = primaryStudioCreative?.creative?.packages?.items || [];
  const creativeId = primaryStudioCreative?.creative?.id || null;
  const bookedPackages = bookingData?.creativePackages || [];

  useEffect(() => {
    if (open) {
      document.body.style.overflow = 'hidden';
    } else {
      document.body.style.overflow = '';
    }
    return () => {
      document.body.style.overflow = '';
    };
  }, [open]);

  useEffect(() => {
    const checkScrollButtons = () => {
      if (scrollContainerRef.current) {
        const { scrollLeft, scrollWidth, clientWidth } = scrollContainerRef.current;
        setCanScrollLeft(scrollLeft > 0);
        setCanScrollRight(scrollLeft < scrollWidth - clientWidth - 1);
      }
    };

    if (open && scrollContainerRef.current) {
      // Check after a short delay to ensure DOM is updated
      const timeoutId = setTimeout(() => {
        checkScrollButtons();
      }, 100);

      scrollContainerRef.current.addEventListener('scroll', checkScrollButtons);
      window.addEventListener('resize', checkScrollButtons);

      return () => {
        clearTimeout(timeoutId);
        if (scrollContainerRef.current) {
          scrollContainerRef.current.removeEventListener('scroll', checkScrollButtons);
        }
        window.removeEventListener('resize', checkScrollButtons);
      };
    }
  }, [open, availablePackages]);

  const selectPackage = (pkg, creativeId) => {
    const existing = bookedPackages.find((p) => p.packageId === pkg.id);

    let nextPackages;
    if (existing) {
      nextPackages = bookedPackages.filter((p) => p.packageId !== pkg.id);
    } else {
      const bookedPackage = {
        creativeId: creativeId,
        packageId: pkg.id,
        packageType: pkg.packageType || 'CUSTOM',
        duration: pkg.duration,
        price: pkg.price,
        specialRequests: '',
      };

      nextPackages = [...bookedPackages, bookedPackage];
    }

    updateBookingData('creativePackages', nextPackages);
  };

  const scrollLeft = () => {
    if (scrollContainerRef.current) {
      const cardWidth = 320 + 40; // card width + gap
      scrollContainerRef.current.scrollBy({
        left: -cardWidth,
        behavior: 'smooth'
      });
    }
  };

  const scrollRight = () => {
    if (scrollContainerRef.current) {
      const cardWidth = 320 + 40; // card width + gap
      scrollContainerRef.current.scrollBy({
        left: cardWidth,
        behavior: 'smooth'
      });
    }
  };

  const handleBackdropClick = (e) => {
    if (e.target === e.currentTarget) {
      handleClose();
    }
  };

  const handleClose = () => {
    setSelectedPackageDetail(null);
    onClose();
  };

  const handleOpenPackage = (pkg) => {
    setSelectedPackageDetail(pkg);
  };

  const handleBackToList = () => {
    setSelectedPackageDetail(null);
  };

  const handleAddPackageFromDetail = () => {
    if (selectedPackageDetail && creativeId) {
      selectPackage(selectedPackageDetail, creativeId);
    }
  };

  if (!open || !studioCreativesData || studioCreativesData.length === 0) return null;

  const creative = primaryStudioCreative?.creative || null;

  if (!creative) return null;

  const isPackageSelected = selectedPackageDetail 
    ? bookedPackages.some((bp) => bp.packageId === selectedPackageDetail.id)
    : false;

  const hasOtherPackagesSelected = selectedPackageDetail
    ? bookedPackages.some((bp) => bp.packageId !== selectedPackageDetail.id)
    : false;

  const getButtonText = () => {
    if (isPackageSelected) {
      return 'Aktuelles Paket';
    }
    if (hasOtherPackagesSelected) {
      return 'Auswählen';
    }
    return 'Hinzufügen';
  };

  const images = selectedPackageDetail?.processedPortfolioImages || selectedPackageDetail?.portfolioImages || [];

  return createPortal(
    <>
      <div className={classes.backdrop} onClick={handleBackdropClick} />
      <div className={classes.modal} onClick={handleBackdropClick}>
        <div className={classes.modalWrapper}>
          <div className={classes.modalContent} onClick={(e) => e.stopPropagation()}>
            {!selectedPackageDetail ? (
            <>
            <header className={classes.modalHeader}>
                <h1 className={`${classes.title} titleSemibold`}>Wähle dein Paket</h1>
                <h1 className={`${classes.titlePhone} mediumLarge`}>Wähle dein Paket</h1>
                <button className={classes.closeButton} onClick={handleClose}>
                <svg xmlns="http://www.w3.org/2000/svg" width="17" height="17" viewBox="0 0 17 17" fill="none">
                    <path fillRule="evenodd" clipRule="evenodd" d="M7.50947 8.5L0 0.990535L0.990535 0L8.5 7.50947L16.0095 0L17 0.990535L9.49054 8.5L17 16.0095L16.0095 17L8.5 9.49054L0.990535 17L0 16.0095L7.50947 8.5Z" fill="var(--black)"/>
                </svg>
                </button>
            </header>
            
            <div className={classes.modalBody}>
                <h3 className='titleSemibold3'>Pakete</h3>
                    <div className={classes.packagesScrollWrapper} ref={scrollContainerRef}>
                    <div className={classes.packagesScroll}>
                            {availablePackages?.map((pkg) => (
                                <ServiceCard 
                                    handleClose={handleClose}
                                    key={pkg.id} 
                                    service={pkg} 
                                    selectPackage={selectPackage}
                                    creativeId={creativeId}
                                    isSelected={bookedPackages.some((bp) => bp.packageId === pkg.id)}
                                    onOpenPackage={handleOpenPackage}
                                />
                        ))}
                    </div>
                </div>
            </div>

            {availablePackages?.length > 0 && (
            <div className={classes.modalFooter}>
                        <div className={classes.scrollButtonsContainer}>
                            <button 
                                className={classes.scrollButton} 
                                onClick={scrollLeft}
                                disabled={!canScrollLeft}
                                aria-label="Scroll left"
                            >
                                <svg xmlns="http://www.w3.org/2000/svg" width="20" height="20" viewBox="0 0 20 20" fill="none">
                                    <path d="M12.5 15L7.5 10L12.5 5" stroke="var(--black)" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                                </svg>
                            </button>
                            <button 
                                className={classes.scrollButton} 
                                onClick={scrollRight}
                                disabled={!canScrollRight}
                                aria-label="Scroll right"
                            >
                                <svg xmlns="http://www.w3.org/2000/svg" width="20" height="20" viewBox="0 0 20 20" fill="none">
                                    <path d="M7.5 15L12.5 10L7.5 5" stroke="var(--black)" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                                </svg>
                            </button>
                        </div>
                    </div>
                )}
              </>
            ) : (
              <>
                <header className={`${classes.modalHeader} ${classes.modalHeaderPhone}`}>
                    <div className={classes.modalHeaderBackButtonContainer} onClick={handleBackToList}>
                        <button className={classes.backButton}>
                            <svg xmlns="http://www.w3.org/2000/svg" width="11" height="20" viewBox="0 0 11 20" fill="none">
                                <path fillRule="evenodd" clipRule="evenodd" d="M0 9.6255L8.99855 0.626953L10.2006 1.82903L2.35773 9.67193L10.2486 18.2204L8.99941 19.3734L0.0172915 9.64279L0 9.6255Z" fill="#1F1F1F"/>
                            </svg>
                        </button>
                        <span className="mediumLarge">Zurück</span>
                    </div>
                    <button className={classes.closeButton} onClick={handleClose}>
                    <svg xmlns="http://www.w3.org/2000/svg" width="17" height="17" viewBox="0 0 17 17" fill="none">
                        <path fillRule="evenodd" clipRule="evenodd" d="M7.50947 8.5L0 0.990535L0.990535 0L8.5 7.50947L16.0095 0L17 0.990535L9.49054 8.5L17 16.0095L16.0095 17L8.5 9.49054L0.990535 17L0 16.0095L7.50947 8.5Z" fill="var(--black)"/>
                    </svg>
                    </button>
                </header>

                <div className={classes.modalImagePhoneWrapper}>
                  {!isPackageSelected && (
                    <button
                      type="button"
                      className={classes.modalPhoneBackButtonContainer}
                      onClick={handleBackToList}
                    >
                      <svg xmlns="http://www.w3.org/2000/svg" width="9" height="18" viewBox="0 0 9 18" fill="none">
                        <path fillRule="evenodd" clipRule="evenodd" d="M0 8.17552L7.90197 0L8.95756 1.09214L2.07028 8.21784L9 15.9849L7.90306 17.0325L0.0119278 8.18786L0 8.17552Z" fill="black"/>
                      </svg>
                    </button>
                  )}
                  {isPackageSelected && (
                    <button
                      type="button"
                      className={classes.modalPhoneCloseButtonContainer}
                      onClick={handleClose}
                    >
                      <svg xmlns="http://www.w3.org/2000/svg" width="17" height="17" viewBox="0 0 17 17" fill="none">
                        <path fillRule="evenodd" clipRule="evenodd" d="M7.50947 8.5L0 0.990535L0.990535 0L8.5 7.50947L16.0095 0L17 0.990535L9.49054 8.5L17 16.0095L16.0095 17L8.5 9.49054L0.990535 17L0 16.0095L7.50947 8.5Z" fill="black"/>
                      </svg>
                    </button>
                  )}
                  <img src={selectedPackageDetail?.processedPortfolioImages[0]} alt={selectedPackageDetail?.name} />
                </div>
                
                <div className={classes.packageDetailBody}>
                    <div className={classes.packageImagesWrapper}>
                        {images.map((image, index) => (
                            <div className={classes.packageImageContainer} key={index}>
                                <img src={image} alt={`${selectedPackageDetail?.name} ${index + 1}`} />
                            </div>
                        ))}
                    </div>
                    <div className={classes.packageDescription}>
                        <div className={classes.packageInfos}>
                            <div className={classes.packageInfosHeader}>
                                <h3 className="titleSemibold">{selectedPackageDetail?.name}</h3>
                                <p className="mediumLarge">{selectedPackageDetail?.price}€ pro Stunde</p>
                            </div>
                            <div className={classes.packageInfosDescription}>
                                <p className="regularMiddle">{selectedPackageDetail?.description}</p>
                                <div className={classes.packageInfosDeliverables}>
                                    <svg xmlns="http://www.w3.org/2000/svg" width="24" height="16" viewBox="0 0 24 16" fill="none">
                                        <g clipPath="url(#clip0_16532_23385)">
                                        <path d="M3.42188 7.83781L5.33187 5.87781C5.56187 5.63781 5.87187 5.50781 6.18187 5.50781H9.11187C9.31187 5.50781 9.51188 5.55781 9.68188 5.65781L11.1819 6.51781C11.3619 6.61781 11.5519 6.66781 11.7519 6.66781H14.9519C15.6519 6.66781 16.2219 7.30781 16.2219 8.10781C16.2219 8.90781 15.6519 9.54781 14.9519 9.54781H9.71187" stroke="#CB1541" strokeWidth="1.2" strokeMiterlimit="10"/>
                                        <path d="M16.3219 7.79909L18.3619 6.35909C18.5519 6.24909 18.7119 6.08909 18.8219 5.87909L19.7319 4.00909C19.8819 3.71909 20.1319 3.50909 20.4319 3.42909L21.2219 3.20909C21.5019 3.12909 21.7919 3.17909 22.0419 3.34909C22.6119 3.71909 22.7719 4.56909 22.3819 5.16909L19.0319 10.4291C18.9619 10.5291 18.8819 10.6191 18.7919 10.6991L16.6519 12.5091C16.4719 12.6691 16.2419 12.7491 16.0119 12.7591L7.92188 13.0191" stroke="#CB1541" strokeWidth="1.2" strokeMiterlimit="10"/>
                                        <path d="M3.42094 7.80898L7.84094 12.679C8.17094 13.049 8.14094 13.619 7.78094 13.949L6.33094 15.269L0.710938 9.06898L2.16094 7.74898C2.53094 7.41898 3.09094 7.43898 3.43094 7.80898H3.42094Z" stroke="#CB1541" strokeWidth="1.2" strokeMiterlimit="10"/>
                                        <path d="M7.6875 3.57V0.92C7.6875 0.69 7.9775 0.5 8.3375 0.5H16.8675C17.2275 0.5 17.5175 0.69 17.5175 0.92V3.57" stroke="#CB1541" strokeWidth="1.2" strokeMiterlimit="10"/>
                                        </g>
                                        <defs>
                                        <clipPath id="clip0_16532_23385">
                                        <rect width="23.09" height="15.98" fill="white"/>
                                        </clipPath>
                                        </defs>
                                    </svg>
                                    {selectedPackageDetail?.includedServices?.map((item, index) => (
                                        <span className="regularMiddle" key={index}>{item}</span>
                                    ))}
                                </div>
                                {selectedPackageDetail?.deliveryTime && (
                                    <div className={classes.packageInfosDeliveryTime}>
                                        <svg xmlns="http://www.w3.org/2000/svg" width="19" height="19" viewBox="0 0 19 19" fill="none">
                                            <path d="M17.5 9.34961C17.5 4.84849 13.8507 1.19922 9.34961 1.19922C4.84849 1.19922 1.19922 4.84849 1.19922 9.34961C1.19922 13.8507 4.84849 17.5 9.34961 17.5C13.8507 17.5 17.5 13.8507 17.5 9.34961ZM9.58594 4.09766V9.74707L13.7178 11.9707L13.1494 13.0273L8.48242 10.5176L8.38574 10.457V10.3975V4.09766H9.58594ZM18.6992 9.34961C18.6992 14.5135 14.5135 18.6992 9.34961 18.6992C4.18575 18.6992 0 14.5135 0 9.34961C0 4.18575 4.18575 0 9.34961 0C14.5135 0 18.6992 4.18575 18.6992 9.34961Z" fill="#CB1541"/>
                                        </svg>
                                        <span className="regularMiddle">{selectedPackageDetail.deliveryTime}</span>
                                    </div>
                                )}
                            </div>
                        </div>
                        <button 
                            onClick={handleAddPackageFromDetail}
                            disabled={isPackageSelected}
                            className={`${classes.packageAddButton} ${isPackageSelected ? classes.packageAddButtonDisabled : ''} mediumLarge`}
                        >
                            {getButtonText()}
                        </button>
                    </div>
                </div>
                {!isPackageSelected && (
                  <div className={classes.packageAddButtonPhoneContainer}>
                    <button 
                        onClick={handleAddPackageFromDetail}
                        className={`${classes.packageAddButtonPhone} mediumLarge`}
                    >
                        {getButtonText()}
                    </button>
                  </div>
                )}
              </>
            )}
          </div>
        </div>
      </div>
    </>,
    document.getElementById("modal") || document.body
  );
};

export default CreativeModal;


